/**
 * @file Main navigation — dropdown toggle with animated transitions.
 */

import { animate } from 'motion';

/** Duration in seconds for a full open or close animation. */
const DURATION = 0.33;

/**
 * Initialises the main navigation dropdown behaviour.
 * Call once on DOMContentLoaded.
 */
export function navDesktop() {
  const nav = document.getElementById('nav-desktop');
  if (!nav) return;

  const toggles = [...nav.querySelectorAll('.nav-desktop__toggle')];
  if (!toggles.length) return;

  toggles.forEach(toggle => initToggle(toggle, toggles));

  document.addEventListener('keydown', e => onDocumentKeydown(e, toggles));
  document.addEventListener('click', e => onDocumentClick(e, nav, toggles));
}

// State helpers

/**
 * Returns the dropdown panel associated with a toggle button.
 *
 * @param {HTMLElement} toggle
 * @returns {HTMLElement|null}
 */
const getDropdown = toggle => toggle.nextElementSibling;

/**
 * Returns true when the dropdown is in its closed (inert) state.
 *
 * @param {HTMLElement} dropdown
 * @returns {boolean}
 */
const isClosed = dropdown => dropdown.hasAttribute('inert');

// Open / close

/**
 * Animates a dropdown panel open.
 * Motion picks up from the current opacity value if an animation is already running.
 *
 * @param {HTMLElement} toggle
 * @param {HTMLElement} dropdown
 */
function animateOpen(toggle, dropdown) {
  dropdown.removeAttribute('inert');
  toggle.setAttribute('aria-expanded', 'true');
  animate(dropdown, { opacity: 1 }, { duration: DURATION });
}

/**
 * Animates a dropdown panel closed.
 * Sets inert immediately so the panel leaves the accessibility tree at once,
 * then fades it out visually. Motion picks up from the current opacity if interrupted.
 *
 * @param {HTMLElement} toggle
 * @param {HTMLElement} dropdown
 * @param {boolean} [returnFocus=false] - When true, returns focus to the toggle immediately.
 */
function animateClose(toggle, dropdown, returnFocus = false) {
  dropdown.setAttribute('inert', '');
  toggle.setAttribute('aria-expanded', 'false');
  if (returnFocus) toggle.focus();
  animate(dropdown, { opacity: 0 }, { duration: DURATION });
}

/**
 * Closes all currently open dropdowns.
 *
 * @param {HTMLElement[]} toggles
 * @param {boolean} [returnFocus=false] - When true, returns focus to the active toggle.
 */
function closeAll(toggles, returnFocus = false) {
  toggles.forEach(toggle => {
    const dropdown = getDropdown(toggle);
    if (dropdown && !isClosed(dropdown)) {
      animateClose(toggle, dropdown, returnFocus);
    }
  });
}

// Per-toggle init

/**
 * Wires click behaviour for a single toggle button.
 *
 * @param {HTMLElement} toggle
 * @param {HTMLElement[]} toggles - All toggles in the nav, used to close siblings.
 */
function initToggle(toggle, toggles) {
  const dropdown = getDropdown(toggle);
  if (!dropdown) return;

  // Enforce closed state regardless of server-rendered attributes.
  dropdown.setAttribute('inert', '');
  dropdown.style.opacity = '0';
  toggle.setAttribute('aria-expanded', 'false');

  toggle.addEventListener('click', () => {
    const opening = isClosed(dropdown);
    closeAll(toggles);
    if (opening) animateOpen(toggle, dropdown);
  });
}

// Document-level listeners

/**
 * Closes all dropdowns on Escape, returning focus to the active toggle.
 *
 * @param {KeyboardEvent} e
 * @param {HTMLElement[]} toggles
 */
function onDocumentKeydown(e, toggles) {
  if (e.key !== 'Escape') return;
  closeAll(toggles, true);
}

/**
 * Closes all dropdowns when the user clicks outside the nav.
 *
 * @param {MouseEvent} e
 * @param {HTMLElement} nav
 * @param {HTMLElement[]} toggles
 */
function onDocumentClick(e, nav, toggles) {
  if (nav.contains(e.target)) return;
  closeAll(toggles);
}
